"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useSearchParams } from "next/navigation";
import { getImageUrl } from "@/utils/utils";
import NipponKoeiAccordion from "@/components/sections/nippon-koei-accordion";
import IdeAccordion from "@/components/sections/ide-accordion";

interface AboutPageAccordionProps {
  aboutData: any;
  lang: "en" | "jp";
}

export default function AboutPageAccordion({
  aboutData,
  lang,
}: AboutPageAccordionProps) {
  const [nipponKoeiOpen, setNipponKoeiOpen] = useState(false);
  const [ideOpen, setIdeOpen] = useState(false);
  const searchParams = useSearchParams();

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;

      if (hash === "#nippon-koei") {
        setTimeout(() => {
          setNipponKoeiOpen(true);
        }, 500);
      } else if (hash === "#ide") {
        setTimeout(() => {
          setIdeOpen(true);
        }, 500);
      }
    };

    const handleInitialHash = () => {
      const hash = window.location.hash;
      if (hash === "#nippon-koei" || hash === "#ide") {
        setTimeout(() => {
          handleHashChange();
        }, 1000);
      } else {
        handleHashChange();
      }
    };

    handleInitialHash();
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [searchParams]);

  return (
    <>
      {/* Company Logos Section */}
      <motion.section
        className="bg-[#F4F5F6] mb-4 px-4 md:px-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={
          {
            hidden: { opacity: 0, y: 60 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.8, ease: "easeOut" },
            },
          } as const
        }
      >
        {/* Nippon Koei Section */}
        {aboutData?.accordion1 && (
          <div
            id="nippon-koei"
            className="relative flex flex-col bg-white max-w-[1317px] mx-auto mb-4 group"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30 mix-blend-luminosity pointer-events-none">
              <svg
                width="1316"
                height="140"
                viewBox="0 0 1316 140"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full object-cover"
                style={{
                  transform: "translate(-1987px, 664px)",
                  width: "3303px",
                  height: "1468px",
                }}
              >
                <g
                  style={{ mixBlendMode: "luminosity" }}
                  opacity="0.3"
                  clipPath="url(#clip0_3019_2457)"
                >
                  <path d="M3303 -664H0V804H3303V-664Z" fill="#F0F0F0" />
                  <mask
                    id="mask0_3019_2457"
                    style={{ maskType: "luminance" }}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="-243"
                    width="968"
                    height="1047"
                  >
                    <path
                      d="M520.908 320.75L0 -242.349V804H967.947L613.743 421.107L520.908 320.75Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0_3019_2457)">
                    <path
                      d="M967.947 -242.349H0V804H967.947V-242.349Z"
                      fill="url(#paint0_linear_3019_2457)"
                    />
                  </g>
                  <defs>
                    <linearGradient
                      id="paint0_linear_3019_2457"
                      x1="416.057"
                      y1="814.896"
                      x2="526.926"
                      y2="-56.9382"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#E8E8E8" />
                      <stop offset="1" stopColor="white" />
                    </linearGradient>
                    <clipPath id="clip0_3019_2457">
                      <rect
                        width="3303"
                        height="1468"
                        fill="white"
                        transform="translate(0 -664)"
                      />
                    </clipPath>
                  </defs>
                </g>
              </svg>
            </div>

            <button
              onClick={() => {
                const newState = !nipponKoeiOpen;
                setNipponKoeiOpen(newState);
              }}
              className="relative z-10 py-12 lg:py-[50px] px-8 lg:px-[50px] cursor-pointer group/accordion"
            >
              <div className="max-w-[1215px] mx-auto">
                <div className="flex justify-between items-center w-full transition-colors rounded-lg">
                  {aboutData?.accordion1?.logo && (
                    <Image
                      src={getImageUrl(aboutData?.accordion1?.logo?.url || "")}
                      alt="Nippon Koei Logo"
                      width={188}
                      height={40}
                      className="object-contain"
                    />
                  )}
                  <div
                    className={`relative flex w-8 h-8 p-1 flex-col justify-center items-center rounded-full border transition-colors ${
                      nipponKoeiOpen
                        ? "bg-primary-800 border-primary-800"
                        : "border-[#C9CDD3]"
                    } group-hover/accordion:bg-primary-800 group-hover/accordion:border-primary-800`}
                  >
                    {/* Horizontal line (always visible) */}
                    <div
                      className={`absolute w-4 h-[2px] rounded-3xl ${
                        nipponKoeiOpen ? "bg-white" : "bg-primary-800"
                      } group-hover/accordion:bg-white`}
                    />
                    {/* Vertical line (rotates on toggle) */}
                    <div
                      className={`absolute w-[2px] h-4 rounded-3xl origin-center transition-transform duration-300 ${
                        nipponKoeiOpen ? "bg-white rotate-90" : "bg-primary-800"
                      } group-hover/accordion:bg-white`}
                    />
                  </div>
                </div>
              </div>
            </button>

            {/* Nippon Koei Accordion Content - Outside the button */}
            <NipponKoeiAccordion
              isOpen={nipponKoeiOpen}
              data={aboutData?.accordion1}
            />

            {/* Progress Bar */}
            <div className="flex h-[2px] bg-gray-25 relative">
              <div
                className={`h-[2px] bg-[#0079bd] absolute left-0 top-0 transition-all duration-500 w-0 group-hover:w-full`}
              ></div>
            </div>
          </div>
        )}

        {/* ID&E Section */}
        {aboutData?.accordion2 && (
          <div
            id="ide"
            className="relative flex flex-col bg-white max-w-[1317px] mx-auto group"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30 mix-blend-luminosity pointer-events-none">
              <svg
                width="1316"
                height="140"
                viewBox="0 0 1316 140"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full object-cover"
                style={{
                  transform: "translate(-1987px, -1098px)",
                  width: "3303px",
                  height: "1468px",
                }}
              >
                <g
                  style={{ mixBlendMode: "luminosity" }}
                  opacity="0.3"
                  clipPath="url(#clip0_3019_2637)"
                >
                  <path d="M3303 -230H0V1238H3303V-230Z" fill="#F0F0F0" />
                  <defs>
                    <clipPath id="clip0_3019_2637">
                      <rect
                        width="3303"
                        height="1468"
                        fill="white"
                        transform="translate(0 -230)"
                      />
                    </clipPath>
                  </defs>
                </g>
              </svg>
            </div>

            <button
              className="relative z-10 py-12 lg:py-[50px] px-8 lg:px-[50px] cursor-pointer group/accordion"
              onClick={() => {
                const newState = !ideOpen;
                setIdeOpen(newState);
              }}
            >
              <div className="max-w-[1215px] mx-auto">
                <div className="flex justify-between items-center w-full transition-colors rounded-lg">
                  {aboutData?.accordion2?.logo && (
                    <Image
                      src={getImageUrl(aboutData?.accordion2?.logo?.url || "")}
                      alt="ID&E Logo"
                      width={108}
                      height={40}
                      className="object-contain"
                    />
                  )}
                  <div
                    className={`relative flex w-8 h-8 p-1 flex-col justify-center items-center rounded-full border transition-colors ${
                      ideOpen
                        ? "bg-primary-800 border-primary-800"
                        : "border-[#C9CDD3]"
                    } group-hover/accordion:bg-primary-800 group-hover/accordion:border-primary-800`}
                  >
                    {/* Horizontal line (always visible) */}
                    <div
                      className={`absolute w-4 h-[2px] rounded-3xl ${
                        ideOpen ? "bg-white" : "bg-primary-800"
                      } group-hover/accordion:bg-white`}
                    />
                    {/* Vertical line (rotates on toggle) */}
                    <div
                      className={`absolute w-[2px] h-4 rounded-3xl origin-center transition-transform duration-300 ${
                        ideOpen ? "bg-white rotate-90" : "bg-primary-800"
                      } group-hover/accordion:bg-white`}
                    />
                  </div>
                </div>
              </div>
            </button>

            {/* IDE Accordion Content - Outside the button */}
            <IdeAccordion isOpen={ideOpen} data={aboutData?.accordion2} />

            {/* Progress Bar */}
            <div className="flex h-[2px] bg-gray-25 relative">
              <div
                className={`h-[2px] bg-[#CDB300] absolute left-0 top-0 transition-all duration-500 w-0 group-hover:w-full`}
              ></div>
            </div>
          </div>
        )}
      </motion.section>
    </>
  );
}
